<template>
    <a
        :href="clickoutLink"
        :style="colorHandler"
        class="cta-button"
        :class="[classHandler, buttonType, shape]"
        :aria-label="ariaLabel"
        target="_blank"
        rel="nofollow"
        @click="onClick()"
    >
        <span>
            <slot />
        </span>
    </a>
</template>

<script>
import { buttonShape, buttonType, getStorybookDefaults } from '@/constants';

function getButtonStorybookControls(componentName, categoryName = '') {
    const category = categoryName || componentName;
    return {
        [`${componentName}Styles`]: {
            table: {
                disable: true,
            },
        },
        [`${componentName}Shape`]: {
            name: 'Shape',
            table: { category },
            options: Object.values(buttonShape),
            type: { name: 'select', labels: buttonShape },
        },
        [`${componentName}ButtonType`]: {
            name: 'ButtonType',
            table: { category },
            options: Object.values(buttonType),
            control: { type: 'select', labels: buttonType },
        },
        [`${componentName}Fill`]: {
            name: 'Color',
            control: { type: 'color' },
            table: { category },
        },
        [`${componentName}TextColor`]: {
            name: 'Text Color',
            control: { type: 'color' },
            table: { category },
        },
        [`${componentName}InactiveFill`]: {
            name: 'InactiveColor',
            control: { type: 'color' },
            table: { category },
        },
    };
}

export function getButtonProps(props, componentName) {
    return {
        shape: props[componentName + 'Shape'],
        buttonType: props[componentName + 'ButtonType'],
        fill: props[componentName + 'Fill'],
        textColor: props[componentName + 'TextColor'],
        inactiveFill: props[componentName + 'InactiveFill'],
    };
}

export function getButtonStorybookHelpers(
    componentName,
    categoryName = '',
    defaultStyles
) {
    return {
        controls: getButtonStorybookControls(componentName, categoryName),
        defaults: defaultStyles
            ? getStorybookDefaults(componentName, defaultStyles)
            : {},
    };
}

export default {
    name: 'CtaButton',
    props: {
        buttonType: {
            type: String,
            default: buttonType.default,
        },
        fill: {
            type: String,
        },
        inactiveFill: {
            type: String,
            default: 'gray',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        clickoutLink: {
            type: String,
        },
        shape: {
            type: String,
            default: buttonShape.rounded,
        },
        small: {
            type: Boolean,
            default: false,
        },
        large: {
            type: Boolean,
            default: false,
        },
        xLarge: {
            type: Boolean,
            default: false,
        },
        ariaLabel: {
            type: String,
            default: '',
        },
        textColor: {
            type: String,
            default: '#FFF',
        },
    },
    computed: {
        colorHandler() {
            const fillStyles = this.fill
                ? {
                      '--primary': this.fill,
                      '--inactive-bg': this.inactiveFill,
                  }
                : {};

            return {
                ...fillStyles,
                '--text-color': this.textColor,
            };
        },
        classHandler() {
            return [this.disabled && '__inactive', this.buttonSize()];
        },
    },
    methods: {
        onClick() {
            this.$emit('clicked');
        },
        buttonSize() {
            switch (true) {
                case this.xLarge:
                    return '__x-large';
                case this.large:
                    return '__large';
                case this.small:
                    return '__small';
            }
            return '';
        },
    },
};
</script>

<style lang="scss" scoped>
/*! purgecss start ignore */
.cta-button {
    display: flex;
    cursor: pointer;
    position: relative;
    border-radius: 5px;
    width: 100%;
    text-align: center;
    color: var(--text-color);
    height: fit-content;

    &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        border-radius: inherit;
        background: var(--primary);
    }

    &.__inactive {
        pointer-events: none;
        opacity: 0.26;
        &::after {
            background: var(--inactive-bg);
        }
    }

    &:hover:after {
        filter: brightness(0.95) drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.15));
    }

    span {
        margin: auto;
        position: relative;
        z-index: 1;
        text-transform: capitalize;
        font-weight: bold;
        line-height: 32px;
        color: var(--text-color) !important;
    }
}

.cta-button.__small span {
    line-height: 26px;
    font-size: 12px;
}

.cta-button.__large span {
    line-height: 40px;
    font-size: 18px;
}

.cta-button.__x-large span {
    line-height: 48px;
    font-size: 20px;
}

.cta-button.square {
    border-radius: 0px;
}

.cta-button.oval {
    border-radius: 30px;
}

.cta-button.outline {
    color: var(--text-color);
    border: 1px solid var(--primary);

    &.__inactive {
        color: var(--inactive-bg);
        border-color: var(--inactive-bg);
        opacity: 0.26;
    }

    &::after {
        background: transparent;
    }
    &:hover::after {
        background: var(--primary);
        opacity: 0.08;
    }
}

.cta-button.text {
    background: transparent;

    span {
        color: var(--text-color);
    }

    &::after {
        opacity: 0;
    }

    &:hover::after {
        background: var(--primary);
        opacity: 0.08;
    }
    &:hover span {
        filter: brightness(0.95);
    }

    &.__inactive {
        opacity: 0.26;
        &::after {
            background: transparent;
        }

        span {
            color: var(--inactive-bg);
        }
    }
}
/*! purgecss end ignore */
</style>
